import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Col, Drawer, Form, Input, Popconfirm, Radio, Row, Select,
  Space, Switch, Table, Tag, Typography, message,
} from 'antd';
import {
  ApiOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined,
} from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import {
  deleteIntegrationConfig,
  getIntegrationConfigs,
  saveIntegrationConfig,
  testIntegrationConnection,
} from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;
const EMPTY = {
  integrationKey: '', label: '', category: 'GENERAL', updateMethod: 'MANUAL',
  enabled: true, provider: '', baseUrl: '', authType: 'API_KEY',
  authHeader: 'x-api-key', authValue: '', clientId: '', clientSecret: '',
  tokenUrl: '', createPath: '', statusPath: '', testPath: '', webhookSecret: '',
  targetModule: '', targetField: '', stageMatchField: '', stageMatchValue: '',
  stageStatusField: '', providerStatusPath: '', providerReferencePath: '',
  statusMappings: [{ providerStatus: '', applicationStatus: '' }],
};
const AUTH_OPTIONS = [
  { label: 'API Key', value: 'API_KEY' },
  { label: 'Bearer Token', value: 'BEARER_TOKEN' },
  { label: 'Basic Authentication', value: 'BASIC_AUTH' },
  { label: 'OAuth 2.0 Client Credentials', value: 'OAUTH2_CLIENT_CREDENTIALS' },
];

function integrationPayload(values) {
  const statusValues = Object.fromEntries(
    (values.statusMappings ?? [])
      .filter((row) => row?.providerStatus && row?.applicationStatus)
      .map((row) => [row.providerStatus.trim(), row.applicationStatus.trim()]),
  );
  const payload = {
    ...values,
    statusMapping: values.updateMethod === 'API' ? {
      targetModule: values.targetModule,
      targetField: values.targetField,
      stageMatchField: values.stageMatchField,
      stageMatchValue: values.stageMatchValue,
      stageStatusField: values.stageStatusField,
      providerStatusPath: values.providerStatusPath,
      providerReferencePath: values.providerReferencePath,
      values: statusValues,
    } : undefined,
  };
  [
    'statusMappings', 'targetModule', 'targetField', 'stageMatchField',
    'stageMatchValue', 'stageStatusField', 'providerStatusPath',
    'providerReferencePath',
  ].forEach((key) => delete payload[key]);
  return payload;
}

function IntegrationEditor({ open, integration, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const method = Form.useWatch('updateMethod', form);
  const authType = Form.useWatch('authType', form);
  const apiMode = method === 'API';
  const oauth = authType === 'OAUTH2_CLIENT_CREDENTIALS';

  useEffect(() => {
    if (!open) return;
    const mapping = integration?.statusMapping ?? {};
    form.setFieldsValue({
      ...EMPTY, ...integration, authValue: '', clientSecret: '', webhookSecret: '',
      targetModule: mapping.targetModule ?? '',
      targetField: mapping.targetField ?? '',
      stageMatchField: mapping.stageMatchField ?? '',
      stageMatchValue: mapping.stageMatchValue ?? '',
      stageStatusField: mapping.stageStatusField ?? '',
      providerStatusPath: mapping.providerStatusPath ?? '',
      providerReferencePath: mapping.providerReferencePath ?? '',
      statusMappings: Object.entries(mapping.values ?? {}).map(([providerStatus, applicationStatus]) => ({
        providerStatus,
        applicationStatus,
      })).concat(Object.keys(mapping.values ?? {}).length ? [] : [{ providerStatus: '', applicationStatus: '' }]),
    });
  }, [form, integration, open]);

  async function save() {
    try {
      const values = await form.validateFields();
      const payload = integrationPayload(values);
      setSaving(true);
      await saveIntegrationConfig(payload);
      message.success('Integration saved');
      onSaved();
    } catch (error) {
      if (!error?.errorFields) message.error(error?.message || 'Could not save integration');
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    try {
      const values = await form.validateFields([
        'integrationKey', 'baseUrl', 'testPath', 'authType', 'authHeader',
        'authValue', 'clientId', 'clientSecret', 'tokenUrl',
      ]);
      setTesting(true);
      const result = await testIntegrationConnection(integrationPayload({
        ...form.getFieldsValue(true),
        ...values,
      }));
      message.success(result?.message || `Connection successful${result?.statusCode ? ` (${result.statusCode})` : ''}`);
    } catch (error) {
      if (!error?.errorFields) message.error(error?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="min(680px, 96vw)"
      title={integration ? `Edit ${integration.label}` : 'Add Integration'}
      destroyOnClose
      extra={(
        <Space>
          {apiMode && <Button loading={testing} onClick={testConnection}>Test Connection</Button>}
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={save}>Save</Button>
        </Space>
      )}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="label" label="Integration Name" rules={[{ required: true }]}>
              <Input placeholder="Background Check" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="integrationKey"
              label="Integration Key"
              rules={[
                { required: true },
                { pattern: /^[a-z][a-z0-9_-]{1,63}$/, message: 'Use lowercase letters, numbers, _ or -' },
              ]}
            >
              <Input placeholder="bgc" disabled={Boolean(integration)} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="category" label="Category">
              <Select options={[
                { value: 'ONBOARDING', label: 'Onboarding' },
                { value: 'HRMS', label: 'HRMS' },
                { value: 'PAYROLL', label: 'Payroll' },
                { value: 'ESIGNATURE', label: 'E-Signature' },
                { value: 'VMS', label: 'VMS' },
                { value: 'GENERAL', label: 'General / Custom' },
              ]} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked"><Switch /></Form.Item>
          </Col>
        </Row>
        <Form.Item name="updateMethod" label="Update Method" rules={[{ required: true }]}>
          <Radio.Group optionType="button" buttonStyle="solid" options={[
            { label: 'Manual Update', value: 'MANUAL' },
            { label: 'API Integration', value: 'API' },
          ]} />
        </Form.Item>
        {!apiMode && <Alert type="info" showIcon message="This integration will use manual updates." />}
        {apiMode && (
          <>
            <Alert
              type={integration?.credentialsConfigured ? 'success' : 'warning'}
              showIcon
              message={integration?.credentialsConfigured ? 'Credentials configured' : 'Credentials required'}
              description="Secrets are write-only. Leave a secret blank while editing to keep its current value."
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col xs={24} md={12}><Form.Item name="provider" label="Provider" rules={[{ required: true }]}><Input placeholder="Provider name" /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="baseUrl" label="API Base URL" rules={[{ required: true, type: 'url' }]}><Input placeholder="https://api.provider.com" /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="createPath" label="Create/Submit Path" rules={[{ required: true }]}><Input placeholder="/v1/requests" /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="statusPath" label="Status Path" rules={[{ required: true }]} tooltip="Use {referenceId} as a placeholder."><Input placeholder="/v1/requests/{referenceId}" /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="testPath" label="Test Connection Path" rules={[{ required: true }]} tooltip="A safe GET endpoint used only to validate URL and credentials."><Input placeholder="/v1/health" /></Form.Item></Col>
            </Row>
            <Form.Item name="authType" label="Authentication Type" rules={[{ required: true }]}><Select options={AUTH_OPTIONS} /></Form.Item>
            {oauth ? (
              <Row gutter={16}>
                <Col xs={24} md={12}><Form.Item name="clientId" label="Client ID" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="clientSecret" label="Client Secret"><Input.Password placeholder={integration?.credentialsConfigured ? 'Unchanged' : 'Required'} /></Form.Item></Col>
                <Col xs={24}><Form.Item name="tokenUrl" label="Token URL" rules={[{ required: true, type: 'url' }]}><Input /></Form.Item></Col>
              </Row>
            ) : (
              <Row gutter={16}>
                <Col xs={24} md={12}><Form.Item name="authHeader" label="Authentication Header"><Input placeholder="x-api-key" /></Form.Item></Col>
                <Col xs={24} md={12}><Form.Item name="authValue" label="API Credential"><Input.Password placeholder={integration?.credentialsConfigured ? 'Unchanged' : 'Required'} /></Form.Item></Col>
              </Row>
            )}
            <Form.Item name="webhookSecret" label="Webhook Signing Secret (optional)"><Input.Password placeholder={integration?.webhookConfigured ? 'Unchanged' : ''} /></Form.Item>
            <AppCard title="Status Mapping">
              <Text type="secondary">
                Map a status from the provider response into a module field or a matching workflow stage.
              </Text>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col xs={24} md={12}>
                  <Form.Item name="targetModule" label="Target Module" rules={[{ required: true }]}>
                    <Input placeholder="onboarding" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="targetField" label="Target Field" rules={[{ required: true }]} tooltip="Use stages for a stage-array update, or a direct field name.">
                    <Input placeholder="stages" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="providerStatusPath" label="Provider Status JSON Path" rules={[{ required: true }]}>
                    <Input placeholder="data.status" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="providerReferencePath" label="Provider Reference JSON Path">
                    <Input placeholder="data.id" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="stageMatchField" label="Stage Match Field">
                    <Input placeholder="stageCode" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="stageMatchValue" label="Stage Match Value">
                    <Input placeholder="BGC" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="stageStatusField" label="Stage Status Field">
                    <Input placeholder="status" />
                  </Form.Item>
                </Col>
              </Row>
              <Text strong>Status Values</Text>
              <Form.List name="statusMappings">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                    {fields.map((field) => (
                      <Row gutter={12} key={field.key}>
                        <Col flex="1 1 220px">
                          <Form.Item {...field} name={[field.name, 'providerStatus']} rules={[{ required: true, message: 'Provider status is required' }]}>
                            <Input placeholder="Provider status, e.g. completed" />
                          </Form.Item>
                        </Col>
                        <Col flex="1 1 220px">
                          <Form.Item {...field} name={[field.name, 'applicationStatus']} rules={[{ required: true, message: 'Application status is required' }]}>
                            <Input placeholder="Zinnext status, e.g. CLEARED" />
                          </Form.Item>
                        </Col>
                        <Col><Button danger onClick={() => remove(field.name)}>Remove</Button></Col>
                      </Row>
                    ))}
                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ providerStatus: '', applicationStatus: '' })}>
                      Add Status Mapping
                    </Button>
                  </Space>
                )}
              </Form.List>
            </AppCard>
          </>
        )}
      </Form>
    </Drawer>
  );
}

export default function IntegrationSettingsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getIntegrationConfigs()
      .then((rows) => setItems(Array.isArray(rows) ? rows : []))
      .catch((error) => message.error(error?.message || 'Could not load integrations'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { queueMicrotask(load); }, [load]);

  async function remove(key) {
    try {
      await deleteIntegrationConfig(key);
      message.success('Integration deleted');
      load();
    } catch (error) {
      message.error(error?.message || 'Could not delete integration');
    }
  }

  const columns = [
    { title: 'Integration', key: 'name', render: (_, row) => <div className="admin-config-primary-cell"><Text strong>{row.label}</Text><Text type="secondary" code>{row.integrationKey}</Text></div> },
    { title: 'Category', dataIndex: 'category', width: 130, render: (value) => value || 'GENERAL' },
    { title: 'Method', dataIndex: 'updateMethod', width: 110, render: (value) => <Tag color={value === 'API' ? 'blue' : 'default'}>{value}</Tag> },
    { title: 'Provider', dataIndex: 'provider', render: (value) => value || '—' },
    { title: 'Enabled', dataIndex: 'enabled', width: 90, render: (value) => <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag> },
    { title: '', key: 'actions', width: 90, render: (_, row) => <Space size={2}><Button type="text" icon={<EditOutlined />} onClick={() => { setEditing(row); setEditorOpen(true); }} /><Popconfirm title="Delete this integration?" onConfirm={() => remove(row.integrationKey)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm></Space> },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard
        icon={<ApiOutlined />}
        title="Integrations"
        extra={<Space><Button icon={<ReloadOutlined />} onClick={load}>Reload</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true); }}>Add Integration</Button></Space>}
      >
        <div className="admin-accent-bar" />
        <Text type="secondary">Configure reusable manual or API integrations for onboarding, payroll, e-signature, VMS, HRMS, and custom workflows.</Text>
      </AppCard>
      <AppCard title={`${items.length} integration${items.length === 1 ? '' : 's'}`}>
        <Table rowKey="integrationKey" columns={columns} dataSource={items} loading={loading} size="small" className="fg-table" />
      </AppCard>
      <IntegrationEditor open={editorOpen} integration={editing} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}
